import * as THREE from "three";
import type { ArcadePalette } from "@/utils/arcade/theme";
import { CAR_SIZE, DRAW_DISTANCE, FOG_END, FOG_START, LANES, PLAYER_AHEAD, PLAYER_SIZE, REAR_AXLE, ROAD_WIDTH, SECTOR_LENGTH, SEGMENT, type DriveHazard, type DriveSim } from "./driveSim";
import type { DriveView } from "./nightDrive";
import { context2d, withAlpha } from "./render";
import type { GameEnv } from "./types";

// Night Drive in real 3D. Everything is unlit (basic materials and small
// shaders) and every repeated object is instanced, so the whole scene is a
// couple of dozen draw calls. The frame is copied into the stage's 2D canvas,
// where the shared bloom, particles and HUD go on top.

const S = 0.01;
const SEG = SEGMENT * S;
const HALF = ROAD_WIDTH * S;
const ROWS = DRAW_DISTANCE + 14;
const CAMERA_HEIGHT = 11;
const ROW_BACK = 14;
const MAX_TRAFFIC = 20;
const MAX_PICKUPS = 18;
const MAX_CONES = 260;
const MAX_BOARDS = 40;
const MAX_SLICKS = 8;
const MAX_PEOPLE = 8;
const MAX_STRIPES = 40;
const MAX_SIGNS = 12;
// The car model's half width and half length, in view units. Every car is
// scaled from these to the footprint the rules use, so what you see is what
// you hit.
const MODEL_HALF_WIDTH = 4.5;
const MODEL_HALF_LENGTH = 8.8;
const MODEL_REAR_AXLE = MODEL_HALF_LENGTH - REAR_AXLE * S;
// Fog runs from FOG_START to FOG_END ahead of the road origin; the camera sits
// this far behind it.
const CAMERA_BACK = 24;
const FOG_NEAR = FOG_START * S + CAMERA_BACK;
const FOG_FAR = FOG_END * S + CAMERA_BACK;
const PEOPLE_COLORS = [0xffd23f, 0x4fc3f7, 0xff8a50, 0xe8e8f0];
const LAMP_EVERY = 5;
const BUILDING_EVERY = 3;
const TOWER_EVERY = 14;
const PALM_EVERY = 7;

// ---------------------------------------------------------------------------
// Renderer pool: WebGL contexts are scarce, so views borrow one and give it back.
const pool: THREE.WebGLRenderer[] = [];
let created = 0;
const MAX_RENDERERS = 3;

function acquireRenderer(): THREE.WebGLRenderer | null {
  const free = pool.pop();
  if (free) return free;
  if (created >= MAX_RENDERERS) return null;
  try {
    const canvas = typeof document !== "undefined" ? document.createElement("canvas") : new OffscreenCanvas(4, 4);
    const renderer = new THREE.WebGLRenderer({ canvas: canvas as HTMLCanvasElement, antialias: true, alpha: false, powerPreference: "high-performance", preserveDrawingBuffer: false });
    renderer.setPixelRatio(1);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    created += 1;
    return renderer;
  } catch {
    return null;
  }
}

function releaseRenderer(renderer: THREE.WebGLRenderer) {
  renderer.setAnimationLoop(null);
  pool.push(renderer);
}

export function webglAvailable(): boolean {
  try {
    const canvas = typeof document !== "undefined" ? document.createElement("canvas") : typeof OffscreenCanvas !== "undefined" ? new OffscreenCanvas(1, 1) : null;
    return Boolean(canvas && (canvas.getContext("webgl2") || canvas.getContext("webgl")));
  } catch {
    return false;
  }
}

// "hsl(210 80% 60%)" or "hsl(210 80% 60% / 0.5)" or "#rrggbb" -> THREE.Color.
export function cssColor(value: string): THREE.Color {
  const hsl = value.match(/hsl\(\s*([\d.]+)[\s,]+([\d.]+)%[\s,]+([\d.]+)%/);
  if (hsl) return new THREE.Color().setHSL(Number(hsl[1]) / 360, Number(hsl[2]) / 100, Number(hsl[3]) / 100, THREE.SRGBColorSpace);
  try {
    return new THREE.Color(value);
  } catch {
    return new THREE.Color(1, 1, 1);
  }
}

const hash = (n: number) => {
  const x = Math.sin(n * 127.1 + 311.7) * 43758.5453;
  return x - Math.floor(x);
};

// ---------------------------------------------------------------------------
// Textures painted once on 2D canvases.

function canvasTexture(env: GameEnv, width: number, height: number, paint: (ctx: CanvasRenderingContext2D, w: number, h: number) => void) {
  const canvas = env.art.make(width, height);
  const ctx = context2d(canvas);
  if (!canvas || !ctx) return null;
  paint(ctx, width, height);
  const texture = new THREE.CanvasTexture(canvas as HTMLCanvasElement);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}

function glowTexture(env: GameEnv) {
  return canvasTexture(env, 64, 64, (ctx, w, h) => {
    const g = ctx.createRadialGradient(w / 2, h / 2, 0, w / 2, h / 2, w / 2);
    g.addColorStop(0, "rgba(255,255,255,1)");
    g.addColorStop(0.25, "rgba(255,255,255,0.55)");
    g.addColorStop(1, "rgba(255,255,255,0)");
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, w, h);
  });
}

function windowsTexture(env: GameEnv, palette: ArcadePalette) {
  return canvasTexture(env, 64, 128, (ctx, w, h) => {
    ctx.fillStyle = "#07070b";
    ctx.fillRect(0, 0, w, h);
    const colors = [palette.primary, palette.accent, palette.text, palette.secondary];
    for (let y = 6; y < h - 4; y += 9) {
      for (let x = 5; x < w - 4; x += 9) {
        const r = hash(x * 7 + y * 13);
        if (r < 0.52) continue;
        ctx.fillStyle = colors[Math.floor(r * 97) % colors.length];
        ctx.globalAlpha = 0.45 + (r - 0.52) * 1.1;
        ctx.fillRect(x, y, 5, 5);
      }
    }
    ctx.globalAlpha = 1;
  });
}

function noteTexture(env: GameEnv, palette: ArcadePalette) {
  return canvasTexture(env, 96, 96, (ctx, w, h) => {
    const g = ctx.createRadialGradient(w / 2, h / 2, 0, w / 2, h / 2, w / 2);
    g.addColorStop(0, withAlpha(palette.accent, 0.9));
    g.addColorStop(0.45, withAlpha(palette.accent, 0.35));
    g.addColorStop(1, withAlpha(palette.accent, 0));
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, w, h);
    ctx.fillStyle = "#fff";
    ctx.strokeStyle = "#fff";
    ctx.lineWidth = 5;
    ctx.beginPath();
    ctx.ellipse(w * 0.42, h * 0.62, 11, 8, -0.4, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.moveTo(w * 0.52, h * 0.6);
    ctx.lineTo(w * 0.52, h * 0.26);
    ctx.quadraticCurveTo(w * 0.66, h * 0.32, w * 0.64, h * 0.44);
    ctx.stroke();
  });
}

function palmTexture(env: GameEnv) {
  return canvasTexture(env, 128, 128, (ctx, w, h) => {
    ctx.clearRect(0, 0, w, h);
    ctx.fillStyle = "#05050a";
    ctx.strokeStyle = "#05050a";
    for (let i = 0; i < 7; i++) {
      const a = -Math.PI / 2 + (i - 3) * 0.42;
      ctx.save();
      ctx.translate(w / 2, h * 0.34);
      ctx.rotate(a + Math.PI / 2);
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.quadraticCurveTo(-10, -h * 0.22, 0, -h * 0.34);
      ctx.quadraticCurveTo(10, -h * 0.22, 0, 0);
      ctx.fill();
      ctx.restore();
    }
    ctx.lineWidth = 6;
    ctx.beginPath();
    ctx.moveTo(w / 2, h * 0.34);
    ctx.quadraticCurveTo(w * 0.46, h * 0.7, w * 0.52, h);
    ctx.stroke();
  });
}

function stripeTexture(env: GameEnv) {
  return canvasTexture(env, 128, 32, (ctx, w, h) => {
    ctx.fillStyle = "#f2f2f2";
    ctx.fillRect(0, 0, w, h);
    ctx.fillStyle = "#d81f38";
    for (let x = -h; x < w + h; x += 32) {
      ctx.beginPath();
      ctx.moveTo(x, h);
      ctx.lineTo(x + 16, h);
      ctx.lineTo(x + 16 + h, 0);
      ctx.lineTo(x + h, 0);
      ctx.closePath();
      ctx.fill();
    }
  });
}

function slickTexture(env: GameEnv, puddle: boolean) {
  return canvasTexture(env, 128, 128, (ctx, w, h) => {
    ctx.clearRect(0, 0, w, h);
    const g = ctx.createRadialGradient(w / 2, h / 2, 0, w / 2, h / 2, w / 2);
    if (puddle) {
      g.addColorStop(0, "rgba(150,190,240,0.75)");
      g.addColorStop(0.65, "rgba(70,100,170,0.6)");
      g.addColorStop(1, "rgba(40,60,120,0)");
    } else {
      g.addColorStop(0, "rgba(6,6,10,0.95)");
      g.addColorStop(0.55, "rgba(50,20,80,0.85)");
      g.addColorStop(0.8, "rgba(20,110,110,0.6)");
      g.addColorStop(1, "rgba(6,6,10,0)");
    }
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.ellipse(w / 2, h / 2, w / 2, h / 2, 0, 0, Math.PI * 2);
    ctx.fill();
  });
}

// Road signs, drawn once: roadworks ahead, merge, crosswalk.
function signTexture(env: GameEnv, kind: "roadworks" | "merge" | "crosswalk") {
  return canvasTexture(env, 128, 128, (ctx, w, h) => {
    ctx.clearRect(0, 0, w, h);
    ctx.save();
    ctx.translate(w / 2, h / 2);
    if (kind === "crosswalk") {
      ctx.fillStyle = "#1e5bd8";
      ctx.fillRect(-58, -58, 116, 116);
      ctx.fillStyle = "#fff";
      ctx.beginPath();
      ctx.moveTo(0, -46);
      ctx.lineTo(48, 42);
      ctx.lineTo(-48, 42);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = "#111";
      ctx.beginPath();
      ctx.arc(2, -12, 7, 0, Math.PI * 2);
      ctx.fill();
      ctx.lineWidth = 7;
      ctx.lineCap = "round";
      ctx.strokeStyle = "#111";
      ctx.beginPath();
      ctx.moveTo(0, -2);
      ctx.lineTo(-2, 16);
      ctx.moveTo(-2, 16);
      ctx.lineTo(-12, 32);
      ctx.moveTo(-2, 16);
      ctx.lineTo(10, 32);
      ctx.moveTo(0, 2);
      ctx.lineTo(12, 12);
      ctx.stroke();
    } else {
      ctx.rotate(Math.PI / 4);
      ctx.fillStyle = "#ffb000";
      ctx.fillRect(-42, -42, 84, 84);
      ctx.lineWidth = 5;
      ctx.strokeStyle = "#111";
      ctx.strokeRect(-37, -37, 74, 74);
      ctx.rotate(-Math.PI / 4);
      ctx.strokeStyle = "#111";
      ctx.fillStyle = "#111";
      ctx.lineWidth = 7;
      ctx.lineCap = "round";
      if (kind === "merge") {
        ctx.beginPath();
        ctx.moveTo(-14, 30);
        ctx.lineTo(-14, 0);
        ctx.lineTo(10, -22);
        ctx.moveTo(10, -22);
        ctx.lineTo(10, -6);
        ctx.moveTo(10, -22);
        ctx.lineTo(-6, -22);
        ctx.stroke();
      } else {
        // A worker with a shovel.
        ctx.beginPath();
        ctx.arc(-4, -22, 7, 0, Math.PI * 2);
        ctx.fill();
        ctx.beginPath();
        ctx.moveTo(-6, -12);
        ctx.lineTo(-8, 8);
        ctx.lineTo(-18, 26);
        ctx.moveTo(-8, 8);
        ctx.lineTo(4, 26);
        ctx.moveTo(-6, -8);
        ctx.lineTo(14, 2);
        ctx.stroke();
        ctx.fillRect(10, 0, 16, 7);
      }
    }
    ctx.restore();
  });
}

function bannerTexture(env: GameEnv, palette: ArcadePalette) {
  return canvasTexture(env, 512, 96, (ctx, w, h) => {
    ctx.fillStyle = "#0b0b10";
    ctx.fillRect(0, 0, w, h);
    ctx.fillStyle = palette.accent;
    ctx.fillRect(0, 0, w, 8);
    ctx.fillRect(0, h - 8, w, 8);
    for (let x = 0; x < w; x += 48) {
      ctx.fillStyle = (x / 48) % 2 ? "#fff" : "#0b0b10";
      ctx.fillRect(x, 8, 24, 10);
      ctx.fillRect(x + 24, h - 18, 24, 10);
    }
    ctx.font = "800 44px Hanson, Poppins, sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillStyle = "#fff";
    ctx.fillText("CHECKPOINT", w / 2, h / 2 + 2);
  });
}

// ---------------------------------------------------------------------------
// Car model: one geometry, paint parts white (tinted per instance), glass
// and tyres dark, so a single instanced mesh draws the whole traffic.

function carGeometry(): THREE.BufferGeometry {
  const parts: Array<{ geo: THREE.BufferGeometry; shade: number }> = [];
  const box = (w: number, h: number, d: number, x: number, y: number, z: number, shade: number) => {
    const geo = new THREE.BoxGeometry(w, h, d);
    geo.translate(x, y, z);
    parts.push({ geo, shade });
  };
  box(8.8, 1.6, 17.4, 0, 1.6, 0, 0.9);
  box(8.4, 1.3, 16, 0, 3.0, 0.2, 1);
  box(6.2, 1.9, 7.6, 0, 4.6, 1.6, 0.06);
  box(6.6, 0.35, 8.2, 0, 5.7, 1.6, 0.95);
  box(8.6, 0.9, 5, 0, 3.8, -5.6, 0.92);
  box(1.4, 0.05, 16.2, 0, 3.66, 0.2, 2.2);
  box(9.2, 0.35, 2.4, 0, 4.4, 7.8, 0.5);
  box(9.2, 0.9, 0.6, 0, 1.2, 8.7, 0.15);
  for (const x of [-3.9, 3.9]) {
    for (const z of [-5.4, 5.4]) {
      const wheel = new THREE.CylinderGeometry(1.5, 1.5, 1.4, 12);
      wheel.rotateZ(Math.PI / 2);
      wheel.translate(x, 1.5, z);
      parts.push({ geo: wheel, shade: 0.05 });
    }
  }
  return mergeWithShade(parts);
}

function carLightsGeometry(): THREE.BufferGeometry {
  const parts: Array<{ geo: THREE.BufferGeometry; shade: number }> = [];
  for (const x of [-3.1, 3.1]) {
    const geo = new THREE.BoxGeometry(2.4, 0.7, 0.3);
    geo.translate(x, 3.1, 8.55);
    parts.push({ geo, shade: 1 });
  }
  const bar = new THREE.BoxGeometry(5.2, 0.25, 0.25);
  bar.translate(0, 3.1, 8.6);
  parts.push({ geo: bar, shade: 0.7 });
  return mergeWithShade(parts);
}

// A traffic cone: black base, orange body, one white band.
function coneGeometry(): THREE.BufferGeometry {
  const base = new THREE.BoxGeometry(2.8, 0.3, 2.8);
  base.translate(0, 0.15, 0);
  const body = new THREE.CylinderGeometry(0.25, 1.25, 3.2, 10);
  body.translate(0, 1.9, 0);
  const band = new THREE.CylinderGeometry(0.72, 0.9, 0.6, 10);
  band.translate(0, 1.95, 0);
  const merged = mergeWithShade([
    { geo: base, shade: 0.12 },
    { geo: body, shade: 1 },
    { geo: band, shade: 3 },
  ]);
  // Shade > 1 marks white; the rest is tinted orange by the vertex colour.
  const colors = merged.getAttribute("color") as THREE.BufferAttribute;
  for (let i = 0; i < colors.count; i++) {
    const v = colors.getX(i);
    if (v > 1.2) colors.setXYZ(i, 1, 1, 1);
    else if (v > 0.3) colors.setXYZ(i, 1 * v, 0.42 * v, 0.08 * v);
  }
  return merged;
}

function mergeWithShade(parts: Array<{ geo: THREE.BufferGeometry; shade: number }>): THREE.BufferGeometry {
  let vertexCount = 0;
  let indexCount = 0;
  for (const { geo } of parts) {
    vertexCount += geo.attributes.position.count;
    indexCount += geo.index ? geo.index.count : geo.attributes.position.count;
  }
  const positions = new Float32Array(vertexCount * 3);
  const colors = new Float32Array(vertexCount * 3);
  const indices = new Uint32Array(indexCount);
  let vOffset = 0;
  let iOffset = 0;
  for (const { geo, shade } of parts) {
    const pos = geo.attributes.position;
    const normal = geo.attributes.normal;
    for (let i = 0; i < pos.count; i++) {
      positions.set([pos.getX(i), pos.getY(i), pos.getZ(i)], (vOffset + i) * 3);
      const facing = normal ? 0.72 + 0.28 * Math.max(normal.getY(i), normal.getZ(i) * 0.6) : 1;
      colors.set([shade * facing, shade * facing, shade * facing], (vOffset + i) * 3);
    }
    if (geo.index) {
      for (let i = 0; i < geo.index.count; i++) indices[iOffset + i] = geo.index.getX(i) + vOffset;
      iOffset += geo.index.count;
    } else {
      for (let i = 0; i < pos.count; i++) indices[iOffset + i] = vOffset + i;
      iOffset += pos.count;
    }
    vOffset += pos.count;
    geo.dispose();
  }
  const merged = new THREE.BufferGeometry();
  merged.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  merged.setAttribute("color", new THREE.BufferAttribute(colors, 3));
  merged.setIndex(new THREE.BufferAttribute(indices, 1));
  return merged;
}

// ---------------------------------------------------------------------------
// Shaders.

const stripVertex = /* glsl */ `
  attribute float aDist;
  attribute float aSide;
  varying float vDist;
  varying float vSide;
  varying float vDepth;
  void main() {
    vDist = aDist;
    vSide = aSide;
    vec4 mv = modelViewMatrix * vec4(position, 1.0);
    vDepth = -mv.z;
    gl_Position = projectionMatrix * mv;
  }
`;

const roadFragment = /* glsl */ `
  uniform vec3 uAsphalt;
  uniform vec3 uEdge;
  uniform vec3 uEdge2;
  uniform vec3 uLane;
  uniform vec3 uFog;
  uniform float uPulse;
  uniform float uBoost;
  uniform float uFogNear;
  uniform float uFogFar;
  varying float vDist;
  varying float vSide;
  varying float vDepth;
  void main() {
    float side = abs(vSide);
    float stripe = step(0.5, fract(vDist * 0.5));
    vec3 col = uAsphalt * (0.88 + 0.12 * stripe);
    col += uEdge * 0.08 * smoothstep(0.55, 1.0, side) * (1.0 + uPulse * 0.5);
    col += uLane * 0.05 * (1.0 - smoothstep(0.0, 0.6, side));
    float dash = step(0.45, fract(vDist * 0.22 + uBoost * 0.0));
    float lane = (1.0 - smoothstep(0.01, 0.022, abs(side - 0.333))) * dash;
    col = mix(col, uLane * 0.85, lane * 0.9);
    float edge = smoothstep(0.95, 0.965, side) * (1.0 - smoothstep(0.995, 1.01, side));
    col = mix(col, uEdge * (0.85 + uPulse * 0.25), edge);
    if (side > 1.01) {
      float checker = step(0.5, fract(vDist * 0.5));
      col = mix(uEdge2 * 0.35, uEdge * (0.5 + uPulse * 0.2), checker);
    }
    float fog = smoothstep(uFogNear, uFogFar, vDepth);
    gl_FragColor = vec4(mix(col, uFog, fog), 1.0);
  }
`;

const groundFragment = /* glsl */ `
  uniform vec3 uBase;
  uniform vec3 uGrid;
  uniform vec3 uFog;
  uniform float uPulse;
  uniform float uFogNear;
  uniform float uFogFar;
  varying float vDist;
  varying float vSide;
  varying float vDepth;
  void main() {
    float along = abs(fract(vDist * 0.125) - 0.5);
    float across = abs(fract(vSide * 1.5) - 0.5);
    float line = max(1.0 - smoothstep(0.0, 0.03, 0.5 - along), 1.0 - smoothstep(0.0, 0.025, 0.5 - across));
    float nearRoad = 1.0 - smoothstep(1.2, 5.0, abs(vSide));
    vec3 col = uBase + uGrid * line * (0.25 + uPulse * 0.2) * (0.4 + 0.6 * (1.0 - nearRoad * 0.5));
    float fog = smoothstep(uFogNear, uFogFar, vDepth);
    gl_FragColor = vec4(mix(col, uFog, fog), 1.0);
  }
`;

const skyVertex = /* glsl */ `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = vec4(position.xy, 0.9999, 1.0);
  }
`;

const skyFragment = /* glsl */ `
  uniform vec3 uTop;
  uniform vec3 uMid;
  uniform vec3 uHorizon;
  uniform vec3 uStar;
  uniform float uHorizonY;
  uniform float uTime;
  uniform float uOffset;
  uniform float uTreble;
  uniform float uAspect;
  varying vec2 vUv;
  float hash21(vec2 p) {
    p = fract(p * vec2(123.34, 456.21));
    p += dot(p, p + 45.32);
    return fract(p.x * p.y);
  }
  void main() {
    float y = vUv.y;
    vec3 col = mix(uMid, uTop, smoothstep(uHorizonY, 1.0, y));
    col = mix(col, uHorizon, (1.0 - smoothstep(uHorizonY - 0.02, uHorizonY + 0.22, y)) * 0.85);
    vec2 grid = vec2((vUv.x + uOffset) * 180.0 * uAspect, y * 180.0);
    vec2 cell = floor(grid);
    float star = hash21(cell);
    vec2 local = fract(grid) - 0.5;
    float twinkle = 0.55 + 0.45 * sin(uTime * 2.0 + star * 40.0);
    float s = step(0.985, star) * (1.0 - smoothstep(0.0, 0.18, length(local))) * twinkle * (0.7 + uTreble);
    col += uStar * s * smoothstep(uHorizonY + 0.05, uHorizonY + 0.35, y);
    gl_FragColor = vec4(col, 1.0);
  }
`;

const sunFragment = /* glsl */ `
  uniform sampler2D uCover;
  uniform float uHasCover;
  uniform vec3 uTop;
  uniform vec3 uBottom;
  uniform vec3 uGlow;
  uniform float uTime;
  uniform float uPulse;
  varying vec2 vUv;
  void main() {
    vec2 p = vUv * 2.0 - 1.0;
    float r = length(p) * 1.6;
    vec3 col = vec3(0.0);
    float alpha = 0.0;
    if (r < 1.0) {
      vec2 coverUv = p * 1.6 * 0.5 + 0.5;
      vec3 cover = uHasCover > 0.5 ? texture2D(uCover, coverUv).rgb : vec3(1.0);
      vec3 tint = mix(uBottom, uTop, smoothstep(-1.0, 1.0, p.y * 1.6));
      col = mix(cover, tint, uHasCover > 0.5 ? 0.42 : 1.0) * (0.78 + uPulse * 0.1);
      float yy = -p.y * 1.6;
      float band = fract(yy * 5.0 - uTime * 0.35);
      float thickness = clamp((yy - 0.05) * 0.55, 0.0, 0.5);
      float cut = yy > 0.05 ? step(band, thickness) : 0.0;
      alpha = 1.0 - cut;
    }
    float halo = exp(-max(r - 1.0, 0.0) * 4.5) * (0.28 + uPulse * 0.12);
    col = col * alpha + uGlow * halo * (1.0 - alpha * 0.9);
    gl_FragColor = vec4(col, max(alpha, halo * 0.85));
  }
`;

const plainVertex = /* glsl */ `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

// ---------------------------------------------------------------------------

export function createDriveView3D(env: GameEnv, sim: DriveSim): DriveView | null {
  const renderer = acquireRenderer();
  if (!renderer) return null;
  const { palette } = env.theme;
  const { state } = sim;

  const colors = {
    bg: cssColor(palette.background),
    deep: cssColor(palette.backgroundDeep),
    primary: cssColor(palette.primary),
    secondary: cssColor(palette.secondary),
    accent: cssColor(palette.accent),
    text: cssColor(palette.text),
    danger: cssColor(palette.danger),
  };
  const fogColor = colors.bg.clone().lerp(colors.secondary, 0.25);

  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(58, 1, 0.5, 2400);
  const disposables: Array<{ dispose(): void }> = [];
  const track = <T extends { dispose(): void }>(item: T) => {
    disposables.push(item);
    return item;
  };

  // Sky
  const skyUniforms = {
    uTop: { value: colors.deep },
    uMid: { value: colors.bg },
    uHorizon: { value: fogColor },
    uStar: { value: colors.text },
    uHorizonY: { value: 0.5 },
    uTime: { value: 0 },
    uOffset: { value: 0 },
    uTreble: { value: 0 },
    uAspect: { value: 1 },
  };
  const sky = new THREE.Mesh(track(new THREE.PlaneGeometry(2, 2)), track(new THREE.ShaderMaterial({ uniforms: skyUniforms, vertexShader: skyVertex, fragmentShader: skyFragment, depthWrite: false, depthTest: false })));
  sky.renderOrder = -10;
  sky.frustumCulled = false;
  scene.add(sky);

  // Cover-art sun
  let coverTexture: THREE.Texture | null = null;
  let coverSource: CanvasImageSource | null = null;
  const sunUniforms = {
    uCover: { value: null as THREE.Texture | null },
    uHasCover: { value: 0 },
    uTop: { value: colors.accent },
    uBottom: { value: colors.primary },
    uGlow: { value: colors.primary },
    uTime: { value: 0 },
    uPulse: { value: 0 },
  };
  const sun = new THREE.Mesh(
    track(new THREE.PlaneGeometry(1, 1)),
    track(new THREE.ShaderMaterial({ uniforms: sunUniforms, vertexShader: plainVertex, fragmentShader: sunFragment, transparent: true, depthWrite: false, fog: false })),
  );
  sun.renderOrder = -5;
  scene.add(sun);

  // Far skyline ring
  const windows = windowsTexture(env, palette);
  if (windows) {
    windows.wrapS = THREE.RepeatWrapping;
    windows.wrapT = THREE.RepeatWrapping;
    track(windows);
  }
  const skylineCount = 70;
  const skylineMaterial = track(new THREE.MeshBasicMaterial({ map: windows ?? undefined, color: 0xffffff, fog: false }));
  const skyline = new THREE.InstancedMesh(track(new THREE.BoxGeometry(1, 1, 1)), skylineMaterial, skylineCount);
  skyline.renderOrder = -4;
  const skylineSeeds = Array.from({ length: skylineCount }, (_, i) => ({ x: (i / skylineCount - 0.5) * 2, w: 18 + hash(i) * 30, h: 30 + hash(i + 50) * 110, d: 20 + hash(i + 90) * 20 }));
  scene.add(skyline);

  // Road and ground strips
  const makeStrip = (rows: number, fragment: string, uniforms: Record<string, THREE.IUniform>) => {
    const geometry = new THREE.BufferGeometry();
    const vertices = rows * 2;
    geometry.setAttribute("position", new THREE.BufferAttribute(new Float32Array(vertices * 3), 3).setUsage(THREE.DynamicDrawUsage));
    geometry.setAttribute("aDist", new THREE.BufferAttribute(new Float32Array(vertices), 1).setUsage(THREE.DynamicDrawUsage));
    geometry.setAttribute("aSide", new THREE.BufferAttribute(new Float32Array(vertices), 1));
    const index: number[] = [];
    for (let i = 0; i < rows - 1; i++) {
      const a = i * 2;
      index.push(a, a + 2, a + 1, a + 1, a + 2, a + 3);
    }
    geometry.setIndex(index);
    const material = new THREE.ShaderMaterial({ uniforms, vertexShader: stripVertex, fragmentShader: fragment, side: THREE.DoubleSide });
    track(geometry);
    track(material);
    const mesh = new THREE.Mesh(geometry, material);
    mesh.frustumCulled = false;
    return mesh;
  };
  const fogUniforms = { uFog: { value: fogColor }, uFogNear: { value: FOG_NEAR }, uFogFar: { value: FOG_FAR } };
  const roadUniforms = {
    uAsphalt: { value: new THREE.Color(0x0e0e16) },
    uEdge: { value: colors.primary },
    uEdge2: { value: colors.secondary },
    uLane: { value: colors.text },
    uPulse: { value: 0 },
    uBoost: { value: 0 },
    ...fogUniforms,
  };
  const groundUniforms = { uBase: { value: colors.deep.clone().multiplyScalar(0.8) }, uGrid: { value: colors.primary }, uPulse: { value: 0 }, ...fogUniforms };
  const road = makeStrip(ROWS, roadFragment, roadUniforms);
  const groundLeft = makeStrip(ROWS, groundFragment, groundUniforms);
  const groundRight = makeStrip(ROWS, groundFragment, groundUniforms);
  scene.add(groundLeft, groundRight, road);
  const sideAttr = (mesh: THREE.Mesh, left: number, right: number) => {
    const attr = mesh.geometry.getAttribute("aSide") as THREE.BufferAttribute;
    for (let i = 0; i < ROWS; i++) {
      attr.setX(i * 2, left);
      attr.setX(i * 2 + 1, right);
    }
    attr.needsUpdate = true;
  };
  sideAttr(road, -1.12, 1.12);
  sideAttr(groundLeft, -14, -1.1);
  sideAttr(groundRight, 1.1, 14);

  const fogged = (color: THREE.Color) => track(new THREE.MeshBasicMaterial({ color, fog: true }));
  scene.fog = new THREE.Fog(fogColor, FOG_NEAR, FOG_FAR);

  // Roadside buildings with lit windows
  const buildingSlots = Math.ceil(ROWS / BUILDING_EVERY) * 2;
  const buildingTexture = windows ? windows.clone() : null;
  if (buildingTexture) {
    buildingTexture.repeat.set(3, 5);
    buildingTexture.needsUpdate = true;
    track(buildingTexture);
  }
  const buildingMaterial = track(new THREE.MeshBasicMaterial({ map: buildingTexture ?? undefined, color: new THREE.Color(0.42, 0.42, 0.5) }));
  const buildings = new THREE.InstancedMesh(track(new THREE.BoxGeometry(1, 1, 1)), buildingMaterial, buildingSlots);
  buildings.frustumCulled = false;
  scene.add(buildings);

  // Street lamps: pole, arm light and a glow billboard
  const lampSlots = Math.ceil(ROWS / LAMP_EVERY) * 2;
  const poles = new THREE.InstancedMesh(track(new THREE.BoxGeometry(0.5, 16, 0.5)), fogged(new THREE.Color(0x2a2a36)), lampSlots);
  const heads = new THREE.InstancedMesh(track(new THREE.BoxGeometry(3.6, 0.5, 1)), fogged(colors.text.clone().multiplyScalar(0.7)), lampSlots);
  const glow = glowTexture(env);
  if (glow) track(glow);
  const glowMaterial = (color: THREE.Color, opacity = 0.9) =>
    track(new THREE.MeshBasicMaterial({ map: glow ?? undefined, color, transparent: true, opacity, blending: THREE.AdditiveBlending, depthWrite: false, fog: false }));
  const lampGlowMaterial = glowMaterial(colors.accent, 0.3);
  const lampGlows = new THREE.InstancedMesh(track(new THREE.PlaneGeometry(1, 1)), lampGlowMaterial, lampSlots);
  for (const mesh of [poles, heads, lampGlows]) {
    mesh.frustumCulled = false;
    scene.add(mesh);
  }

  // Palm silhouettes
  const palmTex = palmTexture(env);
  if (palmTex) track(palmTex);
  const palmSlots = Math.ceil(ROWS / PALM_EVERY) * 2;
  const palms = new THREE.InstancedMesh(track(new THREE.PlaneGeometry(1, 1)), track(new THREE.MeshBasicMaterial({ map: palmTex ?? undefined, transparent: true, alphaTest: 0.4, color: 0xffffff, fog: true })), palmSlots);
  palms.frustumCulled = false;
  scene.add(palms);

  // Equalizer towers that bounce to the song
  const towerSlots = Math.ceil(ROWS / TOWER_EVERY) * 2;
  const towerGeometry = new THREE.BoxGeometry(2, 1, 2);
  towerGeometry.translate(0, 0.5, 0);
  {
    const pos = towerGeometry.attributes.position;
    const shades = new Float32Array(pos.count * 3);
    for (let i = 0; i < pos.count; i++) {
      const top = pos.getY(i) > 0.5;
      const c = (top ? colors.primary.clone().lerp(colors.accent, 0.4) : colors.secondary).clone().multiplyScalar(top ? 0.5 : 0.3);
      shades.set([c.r, c.g, c.b], i * 3);
    }
    towerGeometry.setAttribute("color", new THREE.BufferAttribute(shades, 3));
  }
  const towers = new THREE.InstancedMesh(track(towerGeometry), track(new THREE.MeshBasicMaterial({ vertexColors: true, fog: true })), towerSlots * 4);
  towers.frustumCulled = false;
  scene.add(towers);

  // Neon arches from sector 2
  const archSlots = Math.ceil(ROWS / 24) + 1;
  const archGeometry = new THREE.TorusGeometry(HALF * 1.35, 0.45, 6, 40, Math.PI);
  const arches = new THREE.InstancedMesh(track(archGeometry), track(new THREE.MeshBasicMaterial({ color: colors.primary, fog: true })), archSlots);
  arches.frustumCulled = false;
  scene.add(arches);

  // Traffic, pickups and the player
  const bodyGeometry = track(carGeometry());
  const lightsGeometry = track(carLightsGeometry());
  const traffic = new THREE.InstancedMesh(bodyGeometry, track(new THREE.MeshBasicMaterial({ vertexColors: true, fog: true })), MAX_TRAFFIC);
  traffic.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(MAX_TRAFFIC * 3), 3);
  // Tail lights brighten when a car brakes.
  const trafficLights = new THREE.InstancedMesh(lightsGeometry, track(new THREE.MeshBasicMaterial({ color: 0xffffff, fog: true })), MAX_TRAFFIC);
  trafficLights.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(MAX_TRAFFIC * 3), 3);
  // Glow sprites fade with distance by their colour, since additive sprites cannot fog.
  const trafficGlows = new THREE.InstancedMesh(track(new THREE.PlaneGeometry(1, 1)), glowMaterial(new THREE.Color(1, 1, 1), 0.3), MAX_TRAFFIC * 2);
  trafficGlows.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(MAX_TRAFFIC * 2 * 3), 3);
  for (const mesh of [traffic, trafficLights, trafficGlows]) {
    mesh.frustumCulled = false;
    scene.add(mesh);
  }

  const noteTex = noteTexture(env, palette);
  if (noteTex) track(noteTex);
  const pickupMaterial = track(new THREE.MeshBasicMaterial({ map: noteTex ?? undefined, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, fog: false, color: 0xffffff }));
  const pickups = new THREE.InstancedMesh(track(new THREE.PlaneGeometry(1, 1)), pickupMaterial, MAX_PICKUPS);
  pickups.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(MAX_PICKUPS * 3), 3);
  pickups.frustumCulled = false;
  scene.add(pickups);

  // Hazards: cones, barrier boards with amber lamps, slicks, people,
  // crosswalk stripes, signs and the checkpoint gate. All fogged, so they
  // come out of the haze instead of popping in.
  const cones = new THREE.InstancedMesh(track(coneGeometry()), track(new THREE.MeshBasicMaterial({ vertexColors: true, fog: true })), MAX_CONES);
  const stripes = stripeTexture(env);
  if (stripes) {
    stripes.wrapS = THREE.RepeatWrapping;
    stripes.repeat.set(2, 1);
    track(stripes);
  }
  const boards = new THREE.InstancedMesh(track(new THREE.BoxGeometry(1, 2, 0.4)), track(new THREE.MeshBasicMaterial({ map: stripes ?? undefined, color: 0xcfcfcf, fog: true })), MAX_BOARDS);
  const boardLegs = new THREE.InstancedMesh(track(new THREE.BoxGeometry(0.35, 1.2, 0.35)), fogged(new THREE.Color(0x1c1c22)), MAX_BOARDS * 2);
  const lampMaterial = track(new THREE.MeshBasicMaterial({ color: new THREE.Color(1, 0.62, 0.05), fog: true }));
  const boardLamps = new THREE.InstancedMesh(track(new THREE.BoxGeometry(0.7, 0.7, 0.4)), lampMaterial, MAX_BOARDS * 2);
  const closedLane = new THREE.InstancedMesh(track(new THREE.PlaneGeometry(1, 1)), track(new THREE.MeshBasicMaterial({ color: new THREE.Color(0.16, 0.11, 0.02), fog: true })), 4);
  const oilTex = slickTexture(env, false);
  const puddleTex = slickTexture(env, true);
  if (oilTex) track(oilTex);
  if (puddleTex) track(puddleTex);
  const slickMesh = (map: THREE.Texture | null) => new THREE.InstancedMesh(track(new THREE.PlaneGeometry(1, 1)), track(new THREE.MeshBasicMaterial({ map: map ?? undefined, transparent: true, depthWrite: false, fog: true, color: 0xffffff })), MAX_SLICKS);
  const oils = slickMesh(oilTex);
  const puddles = slickMesh(puddleTex);
  const personBody = new THREE.InstancedMesh(track(new THREE.BoxGeometry(1.7, 2.8, 1)), track(new THREE.MeshBasicMaterial({ color: 0xffffff, fog: true })), MAX_PEOPLE);
  personBody.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(MAX_PEOPLE * 3), 3);
  const personBand = new THREE.InstancedMesh(track(new THREE.BoxGeometry(1.76, 0.3, 1.06)), fogged(new THREE.Color(0xf4f4f4)), MAX_PEOPLE);
  const personHead = new THREE.InstancedMesh(track(new THREE.SphereGeometry(0.62, 10, 8)), fogged(new THREE.Color(0xe2b894)), MAX_PEOPLE);
  const personLimb = new THREE.InstancedMesh(track(new THREE.BoxGeometry(0.5, 3, 0.55)), fogged(new THREE.Color(0x24242e)), MAX_PEOPLE * 2);
  const personArm = new THREE.InstancedMesh(track(new THREE.BoxGeometry(0.4, 2.4, 0.45)), track(new THREE.MeshBasicMaterial({ color: 0xffffff, fog: true })), MAX_PEOPLE * 2);
  personArm.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(MAX_PEOPLE * 2 * 3), 3);
  const zebra = new THREE.InstancedMesh(track(new THREE.PlaneGeometry(1, 1)), fogged(new THREE.Color(0.85, 0.85, 0.88)), MAX_STRIPES);
  const signPosts = new THREE.InstancedMesh(track(new THREE.BoxGeometry(0.5, 11, 0.5)), fogged(new THREE.Color(0x44444c)), MAX_SIGNS);
  const signFaces = (["roadworks", "merge", "crosswalk"] as const).map((kind) => {
    const map = signTexture(env, kind);
    if (map) track(map);
    return new THREE.InstancedMesh(track(new THREE.PlaneGeometry(1, 1)), track(new THREE.MeshBasicMaterial({ map: map ?? undefined, transparent: true, alphaTest: 0.3, fog: true, color: 0xffffff })), MAX_SIGNS);
  });
  const banner = bannerTexture(env, palette);
  if (banner) track(banner);
  const gate = new THREE.Group();
  const gatePostGeometry = track(new THREE.BoxGeometry(1.4, 17, 1.4));
  const gatePostMaterial = fogged(new THREE.Color(0x2a2a34));
  for (const side of [-1, 1]) {
    const post = new THREE.Mesh(gatePostGeometry, gatePostMaterial);
    post.position.set(side * HALF * 1.32, 8.5, 0);
    gate.add(post);
  }
  const gateBanner = new THREE.Mesh(track(new THREE.PlaneGeometry(HALF * 2.64, 5)), track(new THREE.MeshBasicMaterial({ map: banner ?? undefined, fog: true, color: 0xffffff, side: THREE.DoubleSide })));
  gateBanner.position.set(0, 15, 0);
  gate.add(gateBanner);
  scene.add(gate);
  const hazardMeshes = [cones, boards, boardLegs, boardLamps, closedLane, oils, puddles, personBody, personBand, personHead, personLimb, personArm, zebra, signPosts, ...signFaces];
  for (const mesh of hazardMeshes) {
    mesh.frustumCulled = false;
    scene.add(mesh);
  }

  // The player's car turns on its rear axle: the group sits on the axle and
  // the body is shifted forward inside it.
  const playerMaterial = track(new THREE.MeshBasicMaterial({ vertexColors: true, color: colors.primary.clone().multiplyScalar(0.62) }));
  const player = new THREE.Group();
  const playerBody = new THREE.Group();
  playerBody.position.z = -MODEL_REAR_AXLE;
  player.add(playerBody);
  const playerScale = new THREE.Vector3((PLAYER_SIZE.halfWidth * HALF) / MODEL_HALF_WIDTH, 1, (PLAYER_SIZE.halfLength * S) / MODEL_HALF_LENGTH);
  playerBody.scale.copy(playerScale);
  playerBody.add(new THREE.Mesh(bodyGeometry, playerMaterial));
  const playerLightsMaterial = track(new THREE.MeshBasicMaterial({ color: new THREE.Color(0.9, 0.12, 0.25) }));
  playerBody.add(new THREE.Mesh(lightsGeometry, playerLightsMaterial));
  const roofMaterial = track(new THREE.MeshBasicMaterial({ color: 0xcccccc }));
  const roof = new THREE.Mesh(track(new THREE.PlaneGeometry(6, 7.6)), roofMaterial);
  roof.rotation.x = -Math.PI / 2;
  roof.position.set(0, 6.25, 1.2);
  playerBody.add(roof);
  const underglow = new THREE.Mesh(track(new THREE.PlaneGeometry(16, 22)), glowMaterial(colors.accent, 0.12));
  underglow.rotation.x = -Math.PI / 2;
  underglow.position.set(0, 0.15, 0);
  playerBody.add(underglow);
  const tailGlowMaterial = glowMaterial(new THREE.Color(1, 0.18, 0.32), 0.35);
  const tailGlows = [-3.1, 3.1].map((x) => {
    const mesh = new THREE.Mesh(track(new THREE.PlaneGeometry(1, 1)), tailGlowMaterial);
    mesh.position.set(x, 3.1, 9);
    playerBody.add(mesh);
    return mesh;
  });
  const flameMaterial = glowMaterial(colors.accent, 0.6);
  const flames = [-2.4, 2.4].map((x) => {
    const mesh = new THREE.Mesh(track(new THREE.PlaneGeometry(1, 1)), flameMaterial);
    mesh.position.set(x, 1.4, 10);
    playerBody.add(mesh);
    return mesh;
  });
  const beam = new THREE.Mesh(track(new THREE.PlaneGeometry(22, 80)), glowMaterial(colors.text.clone().lerp(colors.accent, 0.3), 0.07));
  beam.rotation.x = -Math.PI / 2;
  beam.position.set(0, 0.2, -48);
  playerBody.add(beam);
  scene.add(player);

  // Per-frame row data
  const rowX = new Float32Array(ROWS);
  const rowY = new Float32Array(ROWS);
  const rowZ = new Float32Array(ROWS);
  const matrix = new THREE.Matrix4();
  const quaternion = new THREE.Quaternion();
  const scale = new THREE.Vector3();
  const position = new THREE.Vector3();
  const euler = new THREE.Euler();
  const faceCamera = new THREE.Quaternion();
  const inverse = new THREE.Quaternion();
  let skyOffset = 0;
  let lastPosition = state.position;
  let pixelWidth = 0;
  let pixelHeight = 0;

  const rowAt = (worldZ: number) => {
    const n = (worldZ - state.position) / SEGMENT + (state.position % SEGMENT) / SEGMENT + ROW_BACK;
    const i = Math.floor(n);
    if (i < 0 || i >= ROWS - 1) return null;
    const t = n - i;
    return { x: rowX[i] + (rowX[i + 1] - rowX[i]) * t, y: rowY[i] + (rowY[i + 1] - rowY[i]) * t, z: rowZ[i] + (rowZ[i + 1] - rowZ[i]) * t, n };
  };

  // Same as rowAt without the allocation, for the per object loops.
  const at = { x: 0, y: 0, z: 0 };
  const rowInto = (worldZ: number) => {
    const n = (worldZ - state.position) / SEGMENT + (state.position % SEGMENT) / SEGMENT + ROW_BACK;
    const i = Math.floor(n);
    if (i < 0 || i >= ROWS - 1) return false;
    const t = n - i;
    at.x = rowX[i] + (rowX[i + 1] - rowX[i]) * t;
    at.y = rowY[i] + (rowY[i + 1] - rowY[i]) * t;
    at.z = rowZ[i] + (rowZ[i + 1] - rowZ[i]) * t;
    return true;
  };
  // How much of something shows through the fog, for additive sprites.
  const visible = (z: number) => {
    const depth = CAMERA_BACK - z;
    const t = Math.min(1, Math.max(0, (depth - FOG_NEAR) / (FOG_FAR - FOG_NEAR)));
    return 1 - t * t * (3 - 2 * t);
  };
  const colorCache = new Map<string, THREE.Color>();
  const paint = (value: string) => {
    let color = colorCache.get(value);
    if (!color) {
      color = cssColor(value);
      colorCache.set(value, color);
    }
    return color;
  };
  const tint = new THREE.Color();

  const placeRot = (mesh: THREE.InstancedMesh, index: number, x: number, y: number, z: number, sx: number, sy: number, sz: number, rotX: number, rotY: number, rotZ: number) => {
    euler.set(rotX, rotY, rotZ);
    quaternion.setFromEuler(euler);
    matrix.compose(position.set(x, y, z), quaternion, scale.set(sx, sy, sz));
    mesh.setMatrixAt(index, matrix);
  };

  const place = (mesh: THREE.InstancedMesh, index: number, x: number, y: number, z: number, sx: number, sy: number, sz: number, rotY = 0, rotZ = 0) => {
    euler.set(0, rotY, rotZ);
    quaternion.setFromEuler(euler);
    matrix.compose(position.set(x, y, z), quaternion, scale.set(sx, sy, sz));
    mesh.setMatrixAt(index, matrix);
  };

  const billboard = (mesh: THREE.InstancedMesh, index: number, x: number, y: number, z: number, size: number) => {
    matrix.compose(position.set(x, y, z), faceCamera, scale.set(size, size, size));
    mesh.setMatrixAt(index, matrix);
  };

  const hide = (mesh: THREE.InstancedMesh, from: number) => {
    for (let i = from; i < mesh.count; i++) {
      matrix.makeScale(0, 0, 0);
      mesh.setMatrixAt(i, matrix);
    }
    mesh.instanceMatrix.needsUpdate = true;
  };

  return {
    render(ctx, audio, width, height) {
      const dpr = Math.min(1.5, typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1);
      const w = Math.max(2, Math.round(width * dpr));
      const h = Math.max(2, Math.round(height * dpr));
      if (w !== pixelWidth || h !== pixelHeight) {
        renderer.setSize(w, h, false);
        pixelWidth = w;
        pixelHeight = h;
      }
      const aspect = width / Math.max(1, height);
      camera.aspect = aspect;
      camera.fov = aspect < 0.8 ? 74 : aspect < 1.2 ? 64 : 54;
      camera.updateProjectionMatrix();

      // Cover texture follows whatever the art kit has loaded.
      const cover = env.art.cover;
      if (cover && cover !== coverSource) {
        coverSource = cover;
        coverTexture?.dispose();
        coverTexture = new THREE.Texture(cover as HTMLImageElement);
        coverTexture.colorSpace = THREE.SRGBColorSpace;
        coverTexture.needsUpdate = true;
        sunUniforms.uCover.value = coverTexture;
        sunUniforms.uHasCover.value = 1;
        roofMaterial.map = coverTexture;
        roofMaterial.needsUpdate = true;
      }

      // Road rows: curve and hills accumulate outward from the camera.
      const baseIndex = Math.floor(state.position / SEGMENT);
      const percent = (state.position % SEGMENT) / SEGMENT;
      const cameraHill = (sim.hillAt(baseIndex) + (sim.hillAt(baseIndex + 1) - sim.hillAt(baseIndex)) * percent) * S;
      let x = 0;
      let dx = -sim.curveAt(baseIndex) * percent * S;
      for (let r = 0; r < ROWS; r++) {
        const n = r - ROW_BACK;
        rowX[r] = n <= 0 ? 0 : x;
        rowY[r] = sim.hillAt(baseIndex + n) * S - cameraHill;
        rowZ[r] = -(n - percent) * SEG;
        if (n >= 0) {
          x += dx;
          dx += sim.curveAt(baseIndex + n) * S;
        }
      }
      const fillStrip = (mesh: THREE.Mesh, left: number, right: number, drop: number) => {
        const pos = mesh.geometry.getAttribute("position") as THREE.BufferAttribute;
        const dist = mesh.geometry.getAttribute("aDist") as THREE.BufferAttribute;
        for (let n = 0; n < ROWS; n++) {
          pos.setXYZ(n * 2, rowX[n] + left * HALF, rowY[n] - drop, rowZ[n]);
          pos.setXYZ(n * 2 + 1, rowX[n] + right * HALF, rowY[n] - drop, rowZ[n]);
          dist.setX(n * 2, baseIndex + n - ROW_BACK);
          dist.setX(n * 2 + 1, baseIndex + n - ROW_BACK);
        }
        pos.needsUpdate = true;
        dist.needsUpdate = true;
      };
      fillStrip(road, -1.12, 1.12, 0);
      fillStrip(groundLeft, -14, -1.1, 0.08);
      fillStrip(groundRight, 1.1, 14, 0.08);

      roadUniforms.uPulse.value = audio.pulse;
      roadUniforms.uBoost.value = state.boost;
      groundUniforms.uPulse.value = audio.pulse * 0.8 + audio.bass * 0.3;

      // Camera rides behind the car and leans into steering.
      const car = rowAt(state.position + PLAYER_AHEAD) ?? { x: 0, y: 0, z: -(PLAYER_AHEAD / SEGMENT) * SEG, n: ROW_BACK + 3 };
      const playerX = car.x + state.playerX * HALF;
      const boostKick = state.boost > 0 ? 1 : 0;
      const cameraRow = rowY[ROW_BACK - 12];
      camera.position.set(state.playerX * HALF * 0.9, Math.max(cameraRow, rowY[ROW_BACK]) + CAMERA_HEIGHT + (aspect < 0.8 ? 4 : 0) - boostKick * 1.5, 24 + boostKick * 4);
      camera.lookAt(playerX, car.y + 3, car.z - 60);
      camera.rotateZ(-state.steer * 0.035);
      camera.updateMatrixWorld();
      faceCamera.copy(camera.quaternion);

      // Sky and the cover sun, which drift against the curve for parallax.
      skyOffset += sim.curveAt(baseIndex) * ((state.position - lastPosition) / (SEGMENT * 58)) * 0.002;
      lastPosition = state.position;
      skyUniforms.uTime.value = state.time;
      skyUniforms.uOffset.value = skyOffset;
      skyUniforms.uTreble.value = audio.treble;
      skyUniforms.uAspect.value = aspect;
      const horizonPoint = new THREE.Vector3(camera.position.x, 0, -2000).project(camera);
      skyUniforms.uHorizonY.value = Math.max(0.2, Math.min(0.85, horizonPoint.y * 0.5 + 0.5));
      const sunDistance = 1500;
      const sunSize = 900 * (1 + audio.pulse * 0.03);
      sun.position.set(camera.position.x - Math.sin(skyOffset * 6) * 260, camera.position.y + 230, camera.position.z - sunDistance);
      sun.scale.set(sunSize, sunSize, 1);
      sun.quaternion.copy(camera.quaternion);
      sunUniforms.uTime.value = state.time;
      sunUniforms.uPulse.value = audio.pulse;

      for (let i = 0; i < skylineCount; i++) {
        const seed = skylineSeeds[i];
        const sx = camera.position.x + seed.x * 1500 - Math.sin(skyOffset * 6) * 180;
        place(skyline, i, sx, camera.position.y - 60 + seed.h / 2, camera.position.z - 1250 - hash(i + 7) * 120, seed.w, seed.h, seed.d);
      }
      skyline.instanceMatrix.needsUpdate = true;
      skylineMaterial.color.setScalar(0.42 + audio.treble * 0.15);

      // Roadside dressing anchored to absolute segment indices.
      let b = 0;
      let l = 0;
      let p = 0;
      let t = 0;
      let a = 0;
      for (let n = ROW_BACK; n < ROWS - 1; n++) {
        const index = baseIndex + n - ROW_BACK;
        for (const side of [-1, 1]) {
          if (index % BUILDING_EVERY === 0 && b < buildingSlots) {
            const r = hash(index * 2 + side);
            const bw = 18 + r * 26;
            const bh = 20 + hash(index * 3 + side) * 70;
            const offset = HALF * (4.2 + hash(index + side * 17) * 4.5);
            place(buildings, b++, rowX[n] + side * (offset + bw / 2), rowY[n] + bh / 2 - 1, rowZ[n], bw, bh, 16 + r * 10);
          }
          if (index % LAMP_EVERY === 0 && l < lampSlots) {
            const lx = rowX[n] + side * HALF * 1.32;
            place(poles, l, lx, rowY[n] + 8, rowZ[n], 1, 1, 1);
            place(heads, l, lx - side * 1.6, rowY[n] + 16, rowZ[n], 1, 1, 1);
            billboard(lampGlows, l, lx - side * 1.6, rowY[n] + 16, rowZ[n], 5 + audio.pulse * 1.5);
            l++;
          }
          if (index % PALM_EVERY === 3 && p < palmSlots) {
            const size = 22 + hash(index + side) * 10;
            place(palms, p++, rowX[n] + side * HALF * (2 + hash(index * 5 + side) * 0.8), rowY[n] + size / 2 - 1, rowZ[n], size, size, 1);
          }
          if (index % TOWER_EVERY === 7 && t + 4 <= towerSlots * 4) {
            for (let k = 0; k < 4; k++) {
              const band = audio.spectrum[(Math.abs(index) * 3 + k * 5) % audio.spectrum.length] ?? 0;
              const height = 4 + band * 34 + (k === 1 || k === 2 ? audio.bass * 10 : 0);
              place(towers, t++, rowX[n] + side * (HALF * 2.4 + k * 3.6), rowY[n], rowZ[n], 1, height, 1);
            }
          }
          if (side === 1 && sim.archAt(index) && a < archSlots) {
            place(arches, a++, rowX[n], rowY[n], rowZ[n], 1, 1 + audio.pulse * 0.05, 1);
          }
        }
      }
      hide(buildings, b);
      hide(poles, l);
      hide(heads, l);
      hide(lampGlows, l);
      hide(palms, p);
      hide(towers, t);
      hide(arches, a);
      (arches.material as THREE.MeshBasicMaterial).color.copy(colors.primary).lerp(colors.accent, audio.pulse * 0.5).multiplyScalar(0.7);
      lampGlowMaterial.opacity = 0.22 + audio.pulse * 0.1;

      // Traffic, scaled to the footprints the rules use.
      let c = 0;
      let g = 0;
      for (const other of state.cars) {
        if (c >= MAX_TRAFFIC) break;
        if (!rowInto(other.z)) continue;
        const cx = at.x + other.x * HALF;
        const size = CAR_SIZE[other.kind];
        const sx = (size.halfWidth * HALF) / MODEL_HALF_WIDTH;
        const sz = (size.halfLength * S) / MODEL_HALF_LENGTH;
        const sy = other.kind === "truck" ? 1.8 : other.kind === "racer" ? 0.8 : 1;
        const heading = -other.yaw;
        place(traffic, c, cx, at.y, at.z, sx, sy, sz, heading);
        place(trafficLights, c, cx, at.y, at.z, sx, sy, sz, heading);
        traffic.setColorAt(c, tint.copy(paint(other.color)).multiplyScalar(other.hit ? 0.3 : 0.5));
        const braking = other.accel < -800 ? 1 : 0;
        trafficLights.setColorAt(c, tint.setRGB(0.75 + braking * 0.25, 0.1 + braking * 0.08, 0.18));
        const vis = visible(at.z) * (0.45 + braking * 0.55);
        // The indicator blinks amber on the side the car is moving to.
        const indicator = other.signal > 0 && Math.floor(state.time * 5) % 2 === 0 ? Math.sign(LANES[other.signalLane] - other.x) : 0;
        const fade = visible(at.z);
        for (let side = -1; side <= 1; side += 2) {
          if (side === indicator) trafficGlows.setColorAt(g, tint.setRGB(fade, fade * 0.55, 0));
          else trafficGlows.setColorAt(g, tint.setRGB(vis, vis * 0.15, vis * 0.3));
          billboard(trafficGlows, g++, cx + side * 3.1 * sx, at.y + 3.1 * sy, at.z + 8.8 * sz, side === indicator ? 5 : 3 + braking * 1.5 + audio.bass);
        }
        c++;
      }
      hide(traffic, c);
      hide(trafficLights, c);
      hide(trafficGlows, g);
      if (traffic.instanceColor) traffic.instanceColor.needsUpdate = true;
      if (trafficLights.instanceColor) trafficLights.instanceColor.needsUpdate = true;
      if (trafficGlows.instanceColor) trafficGlows.instanceColor.needsUpdate = true;

      let k = 0;
      for (const pickup of state.pickups) {
        if (k >= MAX_PICKUPS) break;
        // Notes left behind would sweep past the camera huge.
        if (pickup.z < state.position + PLAYER_AHEAD - PLAYER_SIZE.halfLength || !rowInto(pickup.z)) continue;
        const vis = visible(at.z) * 0.8;
        pickups.setColorAt(k, tint.setRGB(vis, vis, vis));
        billboard(pickups, k++, at.x + pickup.x * HALF, at.y + 5 + Math.sin(state.time * 5 + pickup.id) * 0.8, at.z, 7.5 + audio.pulse);
      }
      hide(pickups, k);
      if (pickups.instanceColor) pickups.instanceColor.needsUpdate = true;

      // Hazards.
      let cone = 0;
      let board = 0;
      let lamp = 0;
      let lane = 0;
      let oil = 0;
      let puddle = 0;
      let person = 0;
      let limb = 0;
      const blink = Math.floor(state.time * 2.5) % 2 === 0;
      lampMaterial.color.setRGB(blink ? 1 : 0.35, blink ? 0.62 : 0.22, blink ? 0.05 : 0.02);
      const addCone = (x: number, worldZ: number, lift = 0, tilt = 0, spin = 0) => {
        if (cone >= MAX_CONES || !rowInto(worldZ)) return;
        placeRot(cones, cone++, at.x + x * HALF, at.y + lift, at.z, 1, 1, 1, tilt, spin, tilt * 0.6);
      };
      const addBoard = (x: number, halfWidth: number, worldZ: number) => {
        if (board >= MAX_BOARDS || !rowInto(worldZ)) return;
        const bx = at.x + x * HALF;
        const w = halfWidth * 2 * HALF;
        place(boards, board, bx, at.y + 2.2, at.z, w, 1, 1);
        for (let side = -1; side <= 1; side += 2) {
          place(boardLegs, board * 2 + (side + 1) / 2, bx + side * w * 0.38, at.y + 0.6, at.z, 1, 1, 1);
          if (lamp < MAX_BOARDS * 2) place(boardLamps, lamp++, bx + side * (w / 2 - 0.5), at.y + 3.6, at.z, 1, 1, 1);
        }
        board++;
      };
      const drawZone = (hazard: DriveHazard) => {
        const z0 = hazard.z - hazard.halfLength;
        const z1 = hazard.z + hazard.halfLength;
        const from = Math.max(z0, state.position - SEGMENT * 12);
        const to = Math.min(z1, state.position + FOG_END);
        if (to <= from) return;
        // The closed lane goes darker between its cones.
        if (lane < 4 && rowInto((from + to) / 2)) {
          placeRot(closedLane, lane++, at.x + hazard.x * HALF, at.y + 0.04, at.z, hazard.halfWidth * 2 * HALF, (to - from) * S, 1, -Math.PI / 2, 0, 0);
        }
        for (let z = z1 - ((z1 - z0) % 500); z >= z0; z -= 500) {
          if (z < from || z > to) continue;
          addCone(hazard.x - hazard.halfWidth + 0.03, z);
          addCone(hazard.x + hazard.halfWidth - 0.03, z);
          if (Math.round((z - z0) / 500) % 4 === 2) addBoard(hazard.x, hazard.halfWidth * 0.75, z);
        }
        if (z0 >= from && z0 <= to) addBoard(hazard.x, hazard.halfWidth, z0);
      };
      for (const hazard of state.hazards) {
        if (hazard.kind === "cone") {
          addCone(hazard.x, hazard.z, hazard.lift * 2, hazard.hit ? hazard.phase * 0.6 : 0, hazard.hit ? hazard.phase : 0);
        } else if (hazard.kind === "barrier") {
          addBoard(hazard.x, hazard.halfWidth, hazard.z);
        } else if (hazard.kind === "roadworks") {
          drawZone(hazard);
        } else if (hazard.kind === "slick") {
          if (!rowInto(hazard.z)) continue;
          const mesh = hazard.variant === 0 ? oils : puddles;
          const index = hazard.variant === 0 ? oil++ : puddle++;
          if (index >= MAX_SLICKS) continue;
          placeRot(mesh, index, at.x + hazard.x * HALF, at.y + 0.06, at.z, hazard.halfWidth * 2 * HALF, hazard.halfLength * 2 * S, 1, -Math.PI / 2, 0, 0);
        } else if (hazard.kind === "pedestrian") {
          if (person >= MAX_PEOPLE || !rowInto(hazard.z)) continue;
          const px = at.x + hazard.x * HALF;
          const stride = Math.sin(hazard.phase);
          const way = Math.sign(hazard.vx) || 1;
          // Facing the way they walk, across the road.
          const face = way > 0 ? -Math.PI / 2 : Math.PI / 2;
          const coat = PEOPLE_COLORS[hazard.variant % PEOPLE_COLORS.length];
          place(personBody, person, px, at.y + 4.5, at.z, 1, 1, 1, face);
          personBody.setColorAt(person, tint.setHex(coat));
          place(personBand, person, px, at.y + 4.1, at.z, 1, 1, 1, face);
          place(personHead, person, px, at.y + 6.6, at.z, 1, 1, 1);
          const raise = hazard.hit ? 1 : 0;
          for (let side = -1; side <= 1; side += 2) {
            const swing = stride * 0.5 * side;
            // Legs swing from the hip, arms from the shoulder.
            placeRot(personLimb, limb, px + Math.sin(swing) * 1.5 * way, at.y + 1.6, at.z + side * 0.45, 1, 1, 1, 0, face, swing);
            placeRot(personArm, limb, px - Math.sin(swing) * 1.1 * way, at.y + 4.6 + raise, at.z + side * 1.05, 1, 1, 1, 0, face, -swing + raise * 2.4 * side);
            personArm.setColorAt(limb, tint.setHex(coat));
            limb++;
          }
          person++;
        }
      }
      hide(cones, cone);
      hide(boards, board);
      hide(boardLegs, board * 2);
      hide(boardLamps, lamp);
      hide(closedLane, lane);
      hide(oils, oil);
      hide(puddles, puddle);
      hide(personBody, person);
      hide(personBand, person);
      hide(personHead, person);
      hide(personLimb, limb);
      hide(personArm, limb);
      if (personBody.instanceColor) personBody.instanceColor.needsUpdate = true;
      if (personArm.instanceColor) personArm.instanceColor.needsUpdate = true;

      let stripe = 0;
      for (const walk of state.crosswalks) {
        if (!rowInto(walk.z)) continue;
        for (let bar = -0.95; bar < 0.95 && stripe < MAX_STRIPES; bar += 0.2) {
          placeRot(zebra, stripe++, at.x + (bar + 0.055) * HALF, at.y + 0.05, at.z, 0.11 * HALF, 5.2, 1, -Math.PI / 2, 0, 0);
        }
      }
      hide(zebra, stripe);

      let post = 0;
      const faces = [0, 0, 0];
      for (const sign of state.signs) {
        if (post >= MAX_SIGNS || !rowInto(sign.z)) continue;
        const sx = at.x + sign.side * HALF * 1.5;
        place(signPosts, post++, sx, at.y + 5.5, at.z, 1, 1, 1);
        const which = sign.kind === "roadworks" ? 0 : sign.kind === "merge" ? 1 : 2;
        place(signFaces[which], faces[which]++, sx, at.y + 11.5, at.z + 0.3, 6, 6, 1);
      }
      hide(signPosts, post);
      for (let i = 0; i < signFaces.length; i++) hide(signFaces[i], faces[i]);

      const gateZ = state.sector * SECTOR_LENGTH;
      gate.visible = rowInto(gateZ);
      if (gate.visible) gate.position.set(at.x, at.y, at.z);

      // Player car, turning on its rear axle.
      const axle = rowAt(state.position + PLAYER_AHEAD - PLAYER_SIZE.halfLength + REAR_AXLE) ?? car;
      player.position.set(axle.x + state.playerX * HALF, axle.y, axle.z);
      player.rotation.set(0, -state.yaw, state.roll);
      player.visible = !(state.invulnerable > 0 && Math.floor(state.invulnerable * 12) % 2 === 0);
      player.updateMatrixWorld();
      inverse.copy(player.quaternion).invert();
      const tail = 2.2 + audio.bass * 0.8;
      for (const glowMesh of tailGlows) {
        glowMesh.scale.setScalar(tail);
        glowMesh.quaternion.copy(camera.quaternion).premultiply(inverse);
      }
      for (const flame of flames) {
        const size = state.boost > 0 ? 5 + Math.sin(state.time * 60 + flame.position.x) * 1.2 : 0.001;
        flame.scale.set(size, size * 1.6, 1);
        flame.quaternion.copy(camera.quaternion).premultiply(inverse);
      }
      (underglow.material as THREE.MeshBasicMaterial).opacity = 0.1 + audio.bass * 0.06 + (state.boost > 0 ? 0.12 : 0);

      renderer.setClearColor(colors.deep, 1);
      renderer.render(scene, camera);
      ctx.drawImage(renderer.domElement as CanvasImageSource, 0, 0, width, height);
    },

    dispose() {
      coverTexture?.dispose();
      for (const item of disposables) item.dispose();
      for (const mesh of [skyline, buildings, poles, heads, lampGlows, palms, towers, arches, traffic, trafficLights, trafficGlows, pickups, ...hazardMeshes]) mesh.dispose();
      releaseRenderer(renderer);
    },
  };
}
